import * as React from "react";
import { cn } from "@/lib/utils";
import { Checkbox } from "./checkbox";

export type SortDir = "asc" | "desc";
export interface SortState {
  key: string;
  dir: SortDir;
}

export interface DataTableColumn<T> {
  key: string;
  header: React.ReactNode;
  cell: (row: T) => React.ReactNode;
  /** CSS grid track size, e.g. "minmax(220px,1.4fr)" or "110px". Defaults to "1fr". */
  width?: string;
  /** Makes the column sortable by this value. Nulls sort last in either direction. */
  sortValue?: (row: T) => string | number | null | undefined;
  className?: string;
}

function compare(a: string | number | null | undefined, b: string | number | null | undefined): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), "nl", { numeric: true, sensitivity: "base" });
}

// Grid-based table in the design's style: uppercase 11px header, 15px rows with a
// hover tint, rounded bordered container. Sorting is internal unless `sort` and
// `onSortChange` are passed; clicking a header cycles asc → desc → off. Passing
// `selectedKeys` + `onSelectionChange` adds a leading checkbox column; the header
// checkbox toggles every row in `rows`.
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  rowClassName,
  empty = "Geen resultaten gevonden.",
  sort: controlledSort,
  onSortChange,
  defaultSort = null,
  minWidth,
  className,
  selectedKeys,
  onSelectionChange,
  tour,
  headerTour,
  rowTour,
}: {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  onRowClick?: (row: T) => void;
  rowClassName?: (row: T) => string | undefined;
  empty?: React.ReactNode;
  sort?: SortState | null;
  onSortChange?: (sort: SortState | null) => void;
  defaultSort?: SortState | null;
  /** Minimum table width in px before it scrolls horizontally. */
  minWidth?: number;
  className?: string;
  selectedKeys?: ReadonlySet<string | number>;
  onSelectionChange?: (next: Set<string | number>) => void;
  /** data-tour targets for the help tours (components/help): the table, its header row, and per row. */
  tour?: string;
  headerTour?: string;
  rowTour?: (row: T) => string | undefined;
}) {
  const [internalSort, setInternalSort] = React.useState<SortState | null>(defaultSort);
  const sort = controlledSort !== undefined ? controlledSort : internalSort;
  const setSort = (next: SortState | null) => {
    if (onSortChange) onSortChange(next);
    if (controlledSort === undefined) setInternalSort(next);
  };

  const selection = selectedKeys && onSelectionChange ? { keys: selectedKeys, onChange: onSelectionChange } : null;
  const template = (selection ? "20px " : "") + columns.map((c) => c.width ?? "1fr").join(" ");

  const selectedCount = selection ? rows.filter((r) => selection.keys.has(rowKey(r))).length : 0;
  const headerChecked: boolean | "indeterminate" =
    selectedCount === 0 ? false : selectedCount === rows.length ? true : "indeterminate";
  const toggleAll = () => {
    selection?.onChange(headerChecked === true ? new Set() : new Set(rows.map(rowKey)));
  };
  const toggleRow = (key: string | number) => {
    if (!selection) return;
    const next = new Set(selection.keys);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    selection.onChange(next);
  };

  const sorted = React.useMemo(() => {
    const col = sort && columns.find((c) => c.key === sort.key);
    if (!sort || !col?.sortValue) return rows;
    const get = col.sortValue;
    const out = [...rows].sort((a, b) => {
      const va = get(a), vb = get(b);
      // Keep nulls last regardless of direction.
      if (va == null || vb == null) return compare(va, vb);
      return sort.dir === "asc" ? compare(va, vb) : -compare(va, vb);
    });
    return out;
  }, [rows, sort, columns]);

  const onHeaderClick = (col: DataTableColumn<T>) => {
    if (!col.sortValue) return;
    if (!sort || sort.key !== col.key) setSort({ key: col.key, dir: "asc" });
    else if (sort.dir === "asc") setSort({ key: col.key, dir: "desc" });
    else setSort(null);
  };

  return (
    <div data-tour={tour} className={cn("overflow-x-auto rounded-card border border-waves-line bg-white", className)}>
      <div role="table" style={minWidth ? { minWidth } : undefined}>
        <div
          role="row"
          data-tour={headerTour}
          className="grid items-center gap-3.5 border-b border-waves-line px-6 py-3.5"
          style={{ gridTemplateColumns: template }}
        >
          {selection && (
            <div role="columnheader" className="flex items-center">
              <Checkbox
                aria-label="Selecteer alle rijen"
                checked={headerChecked}
                disabled={rows.length === 0}
                onCheckedChange={toggleAll}
              />
            </div>
          )}
          {columns.map((col) => {
            const active = sort?.key === col.key;
            const label = "text-left text-[11px] font-bold uppercase tracking-label";
            if (!col.sortValue) {
              return (
                <div key={col.key} role="columnheader" className={cn(label, "text-waves-muted", col.className)}>
                  {col.header}
                </div>
              );
            }
            return (
              <button
                key={col.key}
                type="button"
                role="columnheader"
                aria-sort={active ? (sort!.dir === "asc" ? "ascending" : "descending") : "none"}
                onClick={() => onHeaderClick(col)}
                className={cn(label, "cursor-pointer", active ? "text-waves-ink" : "text-waves-muted hover:text-waves-ink", col.className)}
              >
                {col.header} <span aria-hidden>{active ? (sort!.dir === "asc" ? "↑" : "↓") : "↕"}</span>
              </button>
            );
          })}
        </div>

        {sorted.length === 0 ? (
          <div className="px-6 py-11 text-center text-sm text-waves-muted">{empty}</div>
        ) : (
          sorted.map((row, i) => (
            <div
              key={rowKey(row)}
              role="row"
              data-tour={rowTour?.(row)}
              tabIndex={onRowClick ? 0 : undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              onKeyDown={
                onRowClick
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onRowClick(row);
                      }
                    }
                  : undefined
              }
              className={cn(
                "grid items-center gap-3.5 px-6 py-[15px] text-[15px] text-waves-ink",
                i > 0 && "border-t border-waves-line",
                onRowClick && "cursor-pointer hover:bg-waves-subtle focus-visible:bg-waves-mist focus-visible:outline-none",
                selection?.keys.has(rowKey(row)) && "bg-waves-mist",
                rowClassName?.(row)
              )}
              style={{ gridTemplateColumns: template }}
            >
              {selection && (
                // Stop clicks/keys here from reaching the row's open-on-click handler.
                <div
                  role="cell"
                  className="flex items-center"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <Checkbox
                    aria-label="Selecteer rij"
                    checked={selection.keys.has(rowKey(row))}
                    onCheckedChange={() => toggleRow(rowKey(row))}
                  />
                </div>
              )}
              {columns.map((col) => (
                <div key={col.key} role="cell" className={cn("min-w-0", col.className)}>
                  {col.cell(row)}
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
